import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Button, Input, Label } from 'reactstrap'

import { autoCreateShift, selectPlansStatus } from 'slices/plansSlice'
import { ENABLE_DIALOG_ERROR_STATUS_CODES } from 'slices/utils'
import { selectWorkspacesStatus } from 'slices/workspacesSlice'

import { CustomModal, SingleDateRangePicker, ItemEdit } from 'components/common'
import { DEFAULT_SELECTABLE_FUTURE_DAYS_RANGE, DEFAULT_SELECTABLE_PAST_DAYS_RANGE } from 'components/common/constants'
import type { SuggestionItem } from 'components/common/types'

import useBusinessTime from 'hooks/useBusinessTime'

type Props = {
  isOpen: boolean
  onSuccess: () => void
  onCancel: () => void
  currentWorkspaceId?: number
}

const DATE_RANGE_MAX_RANGE = 31

const ShiftAutoCreate = ({ isOpen, onSuccess, onCancel, currentWorkspaceId }: Props) => {
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<SuggestionItem[]>([])
  const [period, setPeriod] = useState<{ start: Date; end: Date }>()
  const [openRangeDatePicker, setOpenRangeDatePicker] = useState(false)
  const [applyScheduleTemplate, setApplyScheduleTemplate] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [modalErrorMessage, setModalErrorMessage] = useState<string | undefined>(undefined)

  const dispatch = useDispatch()
  const { partialWorkspaces } = useSelector(selectWorkspacesStatus, shallowEqual)
  const { isRequesting, errorMessage } = useSelector(selectPlansStatus, shallowEqual)
  const { getWorkDate } = useBusinessTime()
  const workDate = useMemo(() => getWorkDate(dayjs().format('YYYY-MM-DD')), [getWorkDate])

  // Initialize with current workspace when dialog opens
  useEffect(() => {
    if (!isOpen || !currentWorkspaceId) {
      return
    }
    const target = partialWorkspaces.find(workspace => workspace.id === currentWorkspaceId)
    if (target) {
      setSelectedWorkspaces([{ id: currentWorkspaceId, value: target.name }])
    }
  }, [isOpen, currentWorkspaceId, partialWorkspaces])

  // Handle API response
  useEffect(() => {
    if (!submitted || isRequesting) {
      return
    }
    if (errorMessage === '') {
      setModalErrorMessage(undefined)
      onSuccess()
    } else {
      if (ENABLE_DIALOG_ERROR_STATUS_CODES.includes(errorMessage)) {
        setModalErrorMessage(undefined)
      } else {
        setModalErrorMessage('シフト自動作成に失敗しました。')
      }
    }
    setSubmitted(false)
  }, [submitted, isRequesting, errorMessage, onSuccess])

  const handleApprove = useCallback(() => {
    if (!period || selectedWorkspaces.length === 0) {
      return
    }

    const targetWorkspaceIds = selectedWorkspaces.map(w => (typeof w.id === 'number' ? w.id : Number(w.id)))
    dispatch(
      autoCreateShift({
        startDate: dayjs(period.start).format('YYYY-MM-DD'),
        endDate: dayjs(period.end).format('YYYY-MM-DD'),
        targetWorkspaces: targetWorkspaceIds,
        applyTemplate: applyScheduleTemplate,
      })
    )
    setSubmitted(true)
  }, [dispatch, period, selectedWorkspaces, applyScheduleTemplate])

  const handleCancelClick = useCallback(() => {
    setSelectedWorkspaces([])
    setPeriod(undefined)
    setApplyScheduleTemplate(false)
    setModalErrorMessage(undefined)
    setSubmitted(false)
    onCancel()
  }, [onCancel])

  const disabled = useMemo(() => !period || selectedWorkspaces.length === 0, [period, selectedWorkspaces])

  const workspaceItems = useMemo(() => partialWorkspaces.map(w => ({ id: w.id, value: w.name })), [partialWorkspaces])

  return (
    <CustomModal
      isOpen={isOpen}
      title="シフト自動作成"
      onCancel={handleCancelClick}
      onHideNotification={() => setModalErrorMessage(undefined)}
      errorMessage={modalErrorMessage}
      approveLabel="シフト自動作成"
      onApprove={handleApprove}
      approveDisabled={disabled}
      submitName="shift-auto-create-submit"
    >
      <div className="pb-4">
        <div className="mb-3">メンバーに登録されているデフォルトシフトを元にシフトを自動作成します。</div>

        <div className="mb-4">
          <div className="my-2 fw-bold">ワークスペース選択</div>
          <ItemEdit
            items={workspaceItems}
            selectedItems={selectedWorkspaces}
            label="ワークスペースを追加"
            itemName="ワークスペース"
            onChange={setSelectedWorkspaces}
            showAddAllItemsButton
          />
        </div>

        <div className="mb-4">
          <div className="my-2 fw-bold">自動作成する期間設定</div>
          <div className="mb-2">1回で最大31日分の自動作成が可能です。</div>
          <div className="d-flex">
            <Button outline onClick={() => setOpenRangeDatePicker(true)}>
              {period
                ? `${dayjs(period.start).format('YYYY/MM/DD')} - ${dayjs(period.end).format('YYYY/MM/DD')}`
                : '開始日と終了日を決定'}
            </Button>
            <SingleDateRangePicker
              isOpen={openRangeDatePicker}
              from={period && period.start}
              to={period && period.end}
              maxRange={DATE_RANGE_MAX_RANGE}
              minDate={dayjs(workDate).subtract(DEFAULT_SELECTABLE_PAST_DAYS_RANGE, 'day').toDate()}
              maxDate={dayjs(workDate).add(DEFAULT_SELECTABLE_FUTURE_DAYS_RANGE, 'day').toDate()}
              openToDate={dayjs(workDate).toDate()}
              onCancel={() => setOpenRangeDatePicker(false)}
              onChange={(start, end) => {
                setPeriod({ start, end })
                setOpenRangeDatePicker(false)
              }}
            />
          </div>
        </div>

        <div className="mb-4">
          <div className="my-2 fw-bold">その他の設定</div>
          <div className="mb-2 form-check">
            <Input
              id="apply-schedule-template"
              className="form-check-input"
              checked={applyScheduleTemplate}
              type="checkbox"
              onChange={e => setApplyScheduleTemplate(e.target.checked)}
            />
            <Label className="form-check-label" for="apply-schedule-template">
              作業予定テンプレートを反映
            </Label>
          </div>
          <div className="text-danger">
            デフォルトシフト設定に作業予定テンプレートが設定されている場合は設定に則り作業予定が自動で入力されます。すでに作業予定がある場合には上書きされます。
          </div>
        </div>
      </div>
    </CustomModal>
  )
}

export default ShiftAutoCreate
